import React, { Fragment } from "react";
import styled from "@emotion/styled";
import { ImpactShopProductType, projectTypeMapper } from "@ecologi/shared";
import { Heading } from "../Heading";
import { projectTypeVisuals } from "../consts";
import LocationIcon from "@ecologi/assets/icons/location_alt.svg";
import ToolIcon from "@ecologi/assets/icons/tool.svg";

const Card = styled.div`
  padding: 20px;
  padding-bottom: 40px;
  background-color: ${(props) => props.theme.colors.snowdrop};

  @media ${(props) => props.theme.bp.desktopUp} {
    background-color: ${({ theme }) => theme.colors.mistLight};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  gap: 12px 8px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    font-size: 14px;
    align-items: flex-start;
  }

  @media ${({ theme }) => theme.bp.desktopUp} {
    font-size: 16px;
  }
`;

const SolutionCardGrid = styled(CardGrid)`
  grid-template-columns: 32px 1fr;
`;

const Solutions = styled.span`
  display: inline-block;
  max-width: 30px;
  max-height: 30px;
`;

const CardHeading = styled(Heading)`
  font-size: 14px !important;
  margin-bottom: 18px;
`;

const CardContent = styled.p`
  font-size: 14px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    align-self: center;
  }
`;

const CardsWrap = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 32px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media ${({ theme }) => theme.bp.extraWideDesktopUp} {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    margin-top: 0;
  }
`;

export type ImpactShopProductCardProps = {
  className?: string;
  projectInfo: Partial<ImpactShopProductType["display"]>;
};

export const ImpactShopProductPageCards = ({
  projectInfo: { locations, solutions, activities },
}: ImpactShopProductCardProps) => {
  return (
    <CardsWrap>
      {Array.isArray(locations) && locations.length > 0 && (
        <Card>
          <CardHeading as="h3" size={6}>
            Locations
          </CardHeading>
          <CardGrid>
            {locations.map((location, i) => (
              <Fragment key={i}>
                <LocationIcon />
                <CardContent>{location}</CardContent>
              </Fragment>
            ))}
          </CardGrid>
        </Card>
      )}
      {Array.isArray(solutions) && solutions.length > 0 && (
        <Card>
          <CardHeading as="h3" size={6}>
            Solution types
          </CardHeading>
          <SolutionCardGrid>
            {solutions.map((solution, i) => (
              <Fragment key={i}>
                <Solutions>
                  {projectTypeVisuals[solution.toLowerCase()]?.icon}
                </Solutions>
                <CardContent>{projectTypeMapper[solution].title}</CardContent>
              </Fragment>
            ))}
          </SolutionCardGrid>
        </Card>
      )}
      {Array.isArray(activities) && activities.length > 0 && (
        <Card>
          <CardHeading as="h3" size={6}>
            Typical activities
          </CardHeading>
          <CardGrid>
            {activities.map((activity, i) => (
              <Fragment key={i}>
                <ToolIcon />
                <CardContent>{activity}</CardContent>
              </Fragment>
            ))}
          </CardGrid>
        </Card>
      )}
    </CardsWrap>
  );
};
